// TAPINTO® Editor Frontend - Image Component
// Version: 1.0
// Date: 2025-10-01

import React from 'react';
import { useNode } from '@craftjs/core';
import { ComponentProps } from '../../../types';
import { getImageThumbnailUrl } from '../../../utils/helpers';

interface ImageProps extends ComponentProps {
  src: string;
  alt: string;
  width: string;
  height: string;
  objectFit: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
  borderRadius: string;
  shadow: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  caption?: string;
}

export function Image({
  src,
  alt,
  width,
  height,
  objectFit,
  borderRadius,
  shadow,
  caption,
  ...props
}: ImageProps) {
  const {
    connectors: { connect, drag },
    isActive,
    isHovered,
  } = useNode((state) => ({
    isActive: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  };

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      className={`
        inline-block
        ${isActive ? 'ring-2 ring-primary-500' : ''}
        ${isHovered ? 'ring-2 ring-primary-300' : ''}
      `}
      {...props}
    >
      <div className="relative">
        <img
          src={src || '/placeholder-image.jpg'}
          alt={alt}
          className={`
            ${shadowClasses[shadow]}
            ${src ? '' : 'bg-neutral-200'}
          `}
          style={{
            width,
            height,
            objectFit,
            borderRadius,
            display: 'block',
          }}
          onError={(e) => {
            e.currentTarget.src = '/placeholder-image.jpg';
          }}
        />
        {!src && (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-200 text-neutral-500">
            <span className="text-sm">Click to add image</span>
          </div>
        )}
      </div>
      {caption && (
        <p className="text-sm text-neutral-600 mt-2 text-center">
          {caption}
        </p>
      )}
    </div>
  );
}

Image.craft = {
  displayName: 'Image',
  props: {
    src: '',
    alt: 'Image description',
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
    borderRadius: '8px',
    shadow: 'md',
    caption: '',
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
    canMoveOut: () => true,
  },
};
